import type { ReactNode } from 'react';
import { EyeOutlined } from '@ant-design/icons';

import type { FeConfig } from '../../types/config';
import type { CalendarIntervalType, WeekStartDay } from '../../types/calendar';
import type { EventFieldConfig } from '../eventsConfig';
import type { PromoFieldConfig } from '../promoConfig';
import type { FormCascadeRule } from '../formCascadeConfig';
import type { FieldRule } from '../fieldRulesConfig';
import type { PlanFieldConfig } from '../planConfig';
import type { PlanIndexColumnConfig } from '../planIndexColumnsConfig';
import type { CalendarColumnConfig, CalendarBarFieldMap, CalendarBarFieldEntry } from '../calendarColumnsConfig';
import type { RowActionConfig } from '../calendarMenus';
import type { PlanActionMenuSections, PlanActionMenuViews } from '../planActionMenuConfig';
import type { UpliftConfig } from '../upliftConfig';
import type { OptimizePlanConfig } from '../optimizePlanConfig';
import type { OptimizeResultConfig } from '../optimizeResultConfig';
import type { InfeasiblePlanConfig } from '../infeasiblePlanConfig';
import { LABELS, type Labels } from '../labels';
import type { ImportEventFromPlanConfig } from '../importEventFromPlanConfig';
import type { MetricPageConfig } from '../metricPageConfig';
import type { ScenarioComparisonConfig } from '../scenarioComparisonConfig';
import type { PromoLibraryUiConfig } from '../promoLibraryConfig';

export interface LabeledOption {
    label: string;
    value: string;
    resolveLabelFrom?: string;
}

export interface FilterKeyConfig {
    label: string;
    value: string;
    dimension: string;
    resolveLabelFrom?: string;
}

export interface FilterDimensionInfo {
    [dimension: string]: { label: string; key: string };
}

/**
 * The portion of `TpoGenConfig` sourced from `fe_config` (everything except the
 * metric-view-derived `promoMetricsConfig` / `promoSpendsConfig`).
 */
export interface FeDerivedConfig {
    showEditableIndicators: boolean;
    refetchPromoOnEventUpdate: boolean;
    refetchOtherPromosOnPromoSave: boolean;
    carryEventDatesToPromo: boolean;
    requirePromoForEvent: boolean;
    /** PromoActionBar label field (+ optional `fieldValueLabelMaps` lookup). */
    promoListLabelField: CalendarBarFieldEntry;
    eventDetailsFields: EventFieldConfig[];
    promoDetailsFields: EventFieldConfig[];
    promoFields: PromoFieldConfig[];
    upliftConfig: UpliftConfig;
    formCascadeRules: FormCascadeRule[];
    fieldRules: FieldRule[];
    metricRules: FieldRule[];
    planFields: PlanFieldConfig[];
    planIndexColumns: PlanIndexColumnConfig[];
    calendarIntervalOptions: LabeledOption[];
    defaultCalendarInterval: CalendarIntervalType;
    weekStartDay: WeekStartDay;
    allDefaultColumns: CalendarColumnConfig[];
    /** Divider-separated sections; dividers are derived, not authored. */
    defaultRowActionItems: RowActionConfig;
    /** Divider-separated sections; dividers are derived, not authored. */
    planActionMenuItems: PlanActionMenuSections;
    /** Menu key -> surfaces it renders on; an absent key is visible everywhere. */
    planActionMenuViews: PlanActionMenuViews;
    labels: Labels;
    planIndexFilterKeys: FilterKeyConfig[];
    filterDimensionInfo: FilterDimensionInfo;
    calendarFilterKeys: FilterKeyConfig[];
    calendarFilterDimensionInfo: FilterDimensionInfo;
    metricFilterKeys: FilterKeyConfig[];
    metricFilterDimensionInfo: FilterDimensionInfo;
    calendarGroupByOptions: LabeledOption[];
    metricPageConfig: MetricPageConfig;
    promoLibraryUiConfig: PromoLibraryUiConfig;
    optimizePlanConfig: OptimizePlanConfig;
    optimizeResultConfig: OptimizeResultConfig;
    infeasiblePlanConfig: InfeasiblePlanConfig;
    calendarBarFieldMap: CalendarBarFieldMap;
    importEventFromPlanConfig: ImportEventFromPlanConfig;
    fieldValueLabelMaps: Record<string, Record<string, string>>;
    editLock: EditLockConfig;
    scenarioComparisonConfig: ScenarioComparisonConfig;
}

export interface EditLockConfig {
    /** Rollout kill-switch. When false the plan is always editable as before. */
    enabled: boolean;
    /** Seconds remaining at which the session pill enters its warning state. */
    warningThresholdSec: number;
}

const DEFAULT_EDIT_LOCK: EditLockConfig = { enabled: true, warningThresholdSec: 600 };

// Menu item icons arrive as string identifiers (JSON cannot carry React nodes).
// Map the known ones back to elements; unknown identifiers fall through as
// `undefined` (tolerant: a missing icon must not block the module).
const ICON_BY_NAME: Record<string, ReactNode> = {
    EyeOutlined: <EyeOutlined />,
};

type RawMenuItem = Record<string, unknown>;

/**
 * Resolves the sections of a menu config, keeping the nesting intact. Dividers
 * are not items here: they are derived from the section boundaries when the menu
 * is flattened for AntD.
 */
const resolveMenuSections = (sections: RawMenuItem[][]): PlanActionMenuSections =>
    sections.map((section) =>
        section.map((item) => {
            const { icon, ...rest } = item;
            const resolvedIcon = typeof icon === 'string' ? ICON_BY_NAME[icon] : undefined;
            return resolvedIcon ? { ...rest, icon: resolvedIcon } : rest;
        }),
    ) as unknown as PlanActionMenuSections;

/**
 * Backfills label groups added after a customer config was authored.
 *
 * Every other section is required by the schema, but `editSession`, `keepFlags`
 * and `comparisonReport` are optional so that configs predating the edit lock,
 * the keep-flag row actions and the comparison report still validate. Without
 * this backfill they would validate and then crash at first render on
 * `labels.editSession`.
 */
const adaptLabels = (labels: FeConfig['labels']): Labels => ({
    ...(labels as unknown as Labels),
    editSession: {
        ...LABELS.editSession,
        ...((labels as { editSession?: Partial<Labels['editSession']> })?.editSession ?? {}),
    },
    keepFlags: {
        ...LABELS.keepFlags,
        ...((labels as { keepFlags?: Partial<Labels['keepFlags']> })?.keepFlags ?? {}),
    },
    comparisonReport: {
        ...LABELS.comparisonReport,
        ...((labels as { comparisonReport?: Partial<Labels['comparisonReport']> })?.comparisonReport ?? {}),
    },
    optimizePlan: {
        ...LABELS.optimizePlan,
        ...((labels as { optimizePlan?: Partial<Labels['optimizePlan']> })?.optimizePlan ?? {}),
        level: {
            ...LABELS.optimizePlan.level,
            ...((labels as { optimizePlan?: { level?: Partial<Labels['optimizePlan']['level']> } })?.optimizePlan
                ?.level ?? {}),
        },
    },
});

const toCalendarColumns = (cols: Record<string, unknown>[]): CalendarColumnConfig[] =>
    // Default `required` and `defaultSelected` to false when absent (draft configs may omit them).
    cols.map((col) => ({ required: false, defaultSelected: false, ...col })) as unknown as CalendarColumnConfig[];

/**
 * Maps the validated backend `fe_config` to the FE static-config shape. The FE
 * TS types remain the authoritative contract: the backend `fe_config` was
 * derived from them, so each section is cast field-for-field, with two
 * JSON-driven fix-ups (icon identifiers -> nodes, defaulted `required`).
 */
export function adaptFeConfig(fe: FeConfig): FeDerivedConfig {
    return {
        showEditableIndicators: fe.showEditableIndicators,
        refetchPromoOnEventUpdate: fe.refetchPromoOnEventUpdate,
        refetchOtherPromosOnPromoSave: fe.refetchOtherPromosOnPromoSave,
        carryEventDatesToPromo: fe.carryEventDatesToPromo,
        requirePromoForEvent: fe.requirePromoForEvent,
        promoListLabelField: (fe.promoListLabelField ?? {
            field: 'promo_name',
        }) as CalendarBarFieldEntry,
        eventDetailsFields: fe.eventDetailsFields as unknown as EventFieldConfig[],
        promoDetailsFields: fe.promoDetailsFields as unknown as EventFieldConfig[],
        promoFields: fe.promoFields as unknown as PromoFieldConfig[],
        upliftConfig: fe.upliftConfig as unknown as UpliftConfig,
        formCascadeRules: (fe.formCascadeRules ?? []) as unknown as FormCascadeRule[],
        fieldRules: (fe.fieldRules ?? []) as unknown as FieldRule[],
        metricRules: (fe.metricRules ?? []) as unknown as FieldRule[],
        planFields: fe.planFields as unknown as PlanFieldConfig[],
        planIndexColumns: fe.planIndexColumns as unknown as PlanIndexColumnConfig[],
        calendarIntervalOptions: fe.calendarIntervalOptions as unknown as LabeledOption[],
        defaultCalendarInterval: fe.defaultCalendarInterval ?? 'weekly',
        weekStartDay: fe.weekStartDay ?? 'Sunday',
        allDefaultColumns: toCalendarColumns(fe.allDefaultColumns),
        defaultRowActionItems: fe.defaultRowActionItems as unknown as RowActionConfig,
        planActionMenuItems: resolveMenuSections(fe.planActionMenuItems),
        planActionMenuViews: (fe.planActionMenuViews ?? {}) as PlanActionMenuViews,
        labels: adaptLabels(fe.labels),
        planIndexFilterKeys: fe.planIndexFilterKeys as unknown as FilterKeyConfig[],
        filterDimensionInfo: fe.filterDimensionInfo as unknown as FilterDimensionInfo,
        calendarFilterKeys: fe.calendarFilterKeys as unknown as FilterKeyConfig[],
        calendarFilterDimensionInfo: fe.calendarFilterDimensionInfo as unknown as FilterDimensionInfo,
        // Decoupled MetricPage filter registry. Falls back to the calendar
        // equivalents so a customer config that omits these keeps today's behaviour.
        metricFilterKeys: (fe.metricFilterKeys ?? fe.calendarFilterKeys) as unknown as FilterKeyConfig[],
        metricFilterDimensionInfo: (fe.metricFilterDimensionInfo ??
            fe.calendarFilterDimensionInfo) as unknown as FilterDimensionInfo,
        calendarGroupByOptions: fe.calendarGroupByOptions as unknown as LabeledOption[],
        metricPageConfig: fe.metricPageConfig as unknown as MetricPageConfig,
        promoLibraryUiConfig: fe.promoLibraryConfig as unknown as PromoLibraryUiConfig,
        optimizePlanConfig: fe.optimizePlanConfig as unknown as OptimizePlanConfig,
        optimizeResultConfig: fe.optimizeResultConfig as unknown as OptimizeResultConfig,
        infeasiblePlanConfig: fe.infeasiblePlanConfig as unknown as InfeasiblePlanConfig,
        calendarBarFieldMap: fe.calendarBarFieldMap as unknown as CalendarBarFieldMap,
        importEventFromPlanConfig: fe.importEventFromPlanConfig as unknown as ImportEventFromPlanConfig,
        fieldValueLabelMaps: (fe.fieldValueLabelMaps ?? {}) as Record<string, Record<string, string>>,
        editLock: { ...DEFAULT_EDIT_LOCK, ...((fe.editLock ?? {}) as Partial<EditLockConfig>) },
        scenarioComparisonConfig: fe.scenarioComparisonConfig as unknown as ScenarioComparisonConfig,
    };
}
